import { supabase } from '../lib/supabase';

export type SubscriptionPlan = 'free' | 'premium' | 'pro';

export interface Tenant {
  id: string;
  name: string;
  domain: string;
  subscription_plan: SubscriptionPlan;
  settings: {
    timezone?: string;
    defaultLanguage?: string;
    branding: {
      primaryColor: string;
      logo: string;
    };
    features: {
      contests: boolean;
      rewards: boolean;
      dailyPath: boolean;
      instantWin: boolean;
      html5Games?: boolean;
      triviaQuizzes?: boolean;
    };
  };
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface UserTenantAccess {
  tenant: Tenant;
  role: string;
  appUserId: string;
}

export const tenantsApi = {
  async fetchUserAccessibleTenants(authUserId: string): Promise<UserTenantAccess[]> {
    try {
      const { data, error } = await supabase
        .from('app_users')
        .select(`
          id,
          role,
          tenant:tenants!inner (
            id,
            name,
            domain,
            subscription_plan,
            settings,
            is_active,
            created_at,
            updated_at
          )
        `)
        .eq('auth_user_id', authUserId)
        .eq('tenants.is_active', true);

      if (error) throw error;

      return (data || []).map((item: { id: string; role: string; tenant: Tenant }) => ({
        tenant: item.tenant,
        role: item.role,
        appUserId: item.id
      }));
    } catch (error) {
      console.error('Error fetching user accessible tenants:', error);
      throw error;
    }
  },

  async fetchAllTenants(): Promise<Tenant[]> {
    try {
      const { data, error } = await supabase
        .from('tenants')
        .select('*')
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching tenants:', error);
      throw error;
    }
  },

  async fetchTenantById(tenantId: string): Promise<Tenant | null> {
    try {
      const { data, error } = await supabase
        .from('tenants')
        .select('*')
        .eq('id', tenantId)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching tenant:', error);
      return null;
    }
  },

  async updateTenant(tenantId: string, updates: Partial<Tenant>): Promise<Tenant | null> {
    try {
      const { data, error } = await supabase
        .from('tenants')
        .update(updates)
        .eq('id', tenantId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating tenant:', error);
      throw error;
    }
  }
};
